// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import DataTableCellBase, {
    DataTableCellBaseProps,
    HeaderCellTypography,
} from "@steiner/MobiusOverrides/DataTable/DataTableCellBase";
import DataTableContext from "@steiner/MobiusOverrides/DataTable/DataTableContext";
import * as React from "react";
import { css, withTheme } from "styled-components";

export interface DataTableCellProps extends DataTableCellBaseProps {
    /** Props to be passed to the cell's typography child. */
    typographyProps?: TypographyPresentationProps;
    /**
     * Indicates how the `css` property is combined with the default `css` property from the theme.
     * If true, the default css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the default css in the theme.
     */
    mergeCss?: boolean;
    // XNG-Changes
    header?: string;
}

const DataTableCell: React.FC<DataTableCellProps> = ({
    mergeCss,
    css: cellCss = "",
    children,
    typographyProps,
    ...otherProps
}) => {
    const { applyStyledProp } = applyPropBuilder({ ...otherProps }, { component: "dataTable" });
    const { theme } = otherProps;
    const resolvedMergeCss = mergeCss ?? theme?.dataTable.defaultProps?.mergeCss;
    return (
        <DataTableContext.Consumer>
            {({ cellTypographyProps, isMobile, useMobileLayout, usePrintLayout }) => (
                <DataTableCellBase
                    {...otherProps}
                    css={css`
                        ${applyStyledProp(["cssOverrides", "cell"], resolvedMergeCss)}
                        ${cellCss}
                    `}
                    as={isMobile && useMobileLayout ? "div" : "td"}
                    isMobile={isMobile}
                    useMobileLayout={useMobileLayout}
                    usePrintLayout={usePrintLayout}
                >
                    <HeaderCellTypography isMobile={isMobile} useMobileLayout={useMobileLayout}>
                        {otherProps.header}
                    </HeaderCellTypography>
                    {typeof children === "string" || typeof children === "number" ? (
                        <Typography {...cellTypographyProps} {...typographyProps}>
                            {children}
                        </Typography>
                    ) : (
                        children
                    )}
                </DataTableCellBase>
            )}
        </DataTableContext.Consumer>
    );
};

export default withTheme(DataTableCell);
